import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  inject,
  signal,
  viewChild,
} from '@angular/core';

import { CookieConsentService } from '@core/cookies/cookie-consent.service';

const FOCUSABLE_SELECTOR = 'button:not([disabled]), input:not([disabled]), a[href]';

@Component({
  selector: 'app-cookie-preferences-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="cookie-modal__backdrop">
      <div
        #dialog
        class="cookie-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-modal-title"
        aria-describedby="cookie-modal-intro"
        tabindex="-1"
        (keydown)="onKeydown($event)"
      >
        <h2 id="cookie-modal-title" class="cookie-modal__title">Preferencias de cookies</h2>
        <p id="cookie-modal-intro" class="cookie-modal__intro">
          Elija qué cookies desea permitir. Puede cambiar su elección cuando quiera desde el enlace
          "Cookies" al pie de la página.
        </p>

        <div class="cookie-modal__categories">
          <div class="cookie-modal__row">
            <div class="cookie-modal__text">
              <h3 id="cookie-modal-necessary" class="cookie-modal__category">Necesarias</h3>
              <p class="cookie-modal__description">
                Permiten iniciar sesión, usar el carrito y pagar. No se pueden desactivar.
              </p>
            </div>
            <span class="cookie-modal__always">Siempre activas</span>
          </div>

          <div class="cookie-modal__row">
            <div class="cookie-modal__text">
              <h3 id="cookie-modal-analytics" class="cookie-modal__category">Analítica</h3>
              <p class="cookie-modal__description">
                Nos ayudan a entender cómo se usa la web para mejorarla (Google Analytics).
              </p>
            </div>
            <input
              type="checkbox"
              class="cookie-modal__checkbox"
              aria-labelledby="cookie-modal-analytics"
              [checked]="analytics()"
              (change)="setAnalytics($event)"
            />
          </div>

          <div class="cookie-modal__row">
            <div class="cookie-modal__text">
              <h3 id="cookie-modal-marketing" class="cookie-modal__category">Publicidad</h3>
              <p class="cookie-modal__description">
                Permiten mostrarle beneficios y anuncios relevantes en otras plataformas (Meta).
              </p>
            </div>
            <input
              type="checkbox"
              class="cookie-modal__checkbox"
              aria-labelledby="cookie-modal-marketing"
              [checked]="marketing()"
              (change)="setMarketing($event)"
            />
          </div>
        </div>

        <div class="cookie-modal__actions">
          <button type="button" class="cookie-modal__reject" (click)="reject()">
            Rechazar todo
          </button>
          <button type="button" class="cookie-modal__outline" (click)="save()">
            Guardar preferencias
          </button>
          <button type="button" class="cookie-modal__primary" (click)="accept()">
            Aceptar todo
          </button>
        </div>
      </div>
    </div>
  `,
  styles: `
    :host {
      display: contents;
    }

    .cookie-modal__backdrop {
      position: fixed;
      inset: 0;
      z-index: 1100;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
      box-sizing: border-box;
      background: rgba(0, 0, 0, 0.5);
      font-family: 'Avenir Next', sans-serif;
    }

    .cookie-modal {
      box-sizing: border-box;
      width: min(380px, 100%);
      max-height: calc(100dvh - 32px);
      overflow-y: auto;
      padding: 21px;
      border-radius: 16px;
      background: #ffffff;
      color: #1e1e1e;
    }

    .cookie-modal:focus {
      outline: none;
    }

    .cookie-modal__title {
      margin: 0;
      color: #0b234f;
      font-size: 28px;
      font-weight: 700;
      line-height: 40px;
      letter-spacing: 0.32px;
    }

    .cookie-modal__intro {
      margin: 12px 0 0;
      font-size: 15px;
      font-weight: 500;
      line-height: 20px;
    }

    .cookie-modal__categories {
      margin-top: 20px;
      border: 1px solid #a1a1a7;
      border-radius: 10px;
      overflow: hidden;
    }

    .cookie-modal__row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      padding: 16px;
    }

    .cookie-modal__row + .cookie-modal__row {
      border-top: 1px solid #a1a1a7;
    }

    .cookie-modal__text {
      flex: 1;
      min-width: 0;
    }

    .cookie-modal__category {
      margin: 0;
      color: #0b234f;
      font-size: 16px;
      font-weight: 700;
      line-height: 16px;
    }

    .cookie-modal__description {
      margin: 4px 0 0;
      font-size: 14px;
      font-weight: 500;
      line-height: 19px;
    }

    .cookie-modal__always {
      flex-shrink: 0;
      color: #188934;
      font-size: 14px;
      font-weight: 700;
      line-height: 19px;
      text-align: center;
    }

    .cookie-modal__checkbox {
      appearance: none;
      flex-shrink: 0;
      width: 18px;
      height: 18px;
      margin: 0;
      border: 1px solid #a1a1a7;
      border-radius: 4px;
      background-color: #ffffff;
      background-position: center;
      background-repeat: no-repeat;
      background-size: 14px 14px;
      cursor: pointer;
    }

    .cookie-modal__checkbox:checked {
      border-color: #0b234f;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 14 14'%3E%3Cpath d='M2.5 7.5l3 3 6-7' fill='none' stroke='%230B234F' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    }

    .cookie-modal__checkbox:focus-visible,
    .cookie-modal__reject:focus-visible,
    .cookie-modal__outline:focus-visible,
    .cookie-modal__primary:focus-visible {
      outline: 2px solid #0b234f;
      outline-offset: 2px;
    }

    .cookie-modal__actions {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      gap: 22px;
      margin-top: 32px;
    }

    .cookie-modal__reject {
      order: 3;
      flex: 0 0 100%;
      padding: 0;
      border: 0;
      background: none;
      color: #0b234f;
      font-family: inherit;
      font-size: 14px;
      font-weight: 500;
      text-decoration: underline;
      cursor: pointer;
    }

    .cookie-modal__outline,
    .cookie-modal__primary {
      box-sizing: border-box;
      height: 46px;
      padding: 8px 10px;
      border-radius: 10px;
      font-family: inherit;
      font-size: 14px;
      white-space: nowrap;
      cursor: pointer;
    }

    .cookie-modal__outline {
      flex: 0 0 174px;
      border: 1px solid #0b234f;
      background: #ffffff;
      color: #0b234f;
      font-weight: 500;
    }

    .cookie-modal__primary {
      flex: 1 1 0;
      min-width: 0;
      border: 0;
      background: #0b234f;
      color: #e6ebf1;
      font-weight: 600;
    }

    @media (min-width: 768px) {
      .cookie-modal {
        width: min(616px, 100%);
        padding: 32px 40px;
      }

      .cookie-modal__categories {
        margin-top: 20px;
      }

      .cookie-modal__always {
        white-space: nowrap;
      }

      .cookie-modal__actions {
        justify-content: flex-end;
        flex-wrap: nowrap;
      }

      .cookie-modal__reject {
        order: 0;
        flex: 0 0 auto;
      }

      .cookie-modal__primary {
        flex: 0 0 124px;
      }
    }
  `,
})
export class CookiePreferencesModalComponent implements AfterViewInit, OnDestroy {
  private readonly consent = inject(CookieConsentService);
  private readonly dialog = viewChild.required<ElementRef<HTMLElement>>('dialog');
  private readonly returnFocusTo = this.activeHtmlElement();

  protected readonly analytics = signal(this.consent.preferences().analytics);
  protected readonly marketing = signal(this.consent.preferences().marketing);

  ngAfterViewInit(): void {
    this.dialog().nativeElement.focus();
  }

  ngOnDestroy(): void {
    this.returnFocusTo?.focus();
  }

  setAnalytics(event: Event): void {
    this.analytics.set(this.isChecked(event));
  }

  setMarketing(event: Event): void {
    this.marketing.set(this.isChecked(event));
  }

  accept(): void {
    this.consent.acceptAll();
  }

  reject(): void {
    this.consent.rejectAll();
  }

  save(): void {
    this.consent.save({
      necessary: true,
      preferences: false,
      analytics: this.analytics(),
      marketing: this.marketing(),
    });
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.consent.closePreferences();
      return;
    }
    if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  private trapFocus(event: KeyboardEvent): void {
    const dialog = this.dialog().nativeElement;
    const focusable = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    const first = focusable.at(0);
    const last = focusable.at(-1);
    if (!first || !last) return;
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === dialog)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  private isChecked(event: Event): boolean {
    return (event.target as HTMLInputElement).checked;
  }

  private activeHtmlElement(): HTMLElement | null {
    if (typeof document === 'undefined') return null;
    const active = document.activeElement;
    return active instanceof HTMLElement ? active : null;
  }
}
